<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="dynamicRanges"
      :headers="tableHeaders"
      :list="dynamicRanges"
      :total-records="dynamicRangesTotalCount"
      table-id="dynamic-ranges"
      :items-per-page="dynamicRangesItemsPerPage"
      :selected-page="dynamicRangesPageNumber"
      :is-add-new-hidden="true"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="onRefreshData"
      @table-item-clicked="onEdit"
      clickable>
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as DynamicRange)" />
        </div>
      </template>
    </BaseTable>

    <DynamicRangeEditModal
      v-if="createEditModalActive"
      :dynamic-range="currentDynamicRange!"
      :title="$t('ranges.editDynamicRange')"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import type { QueryParams, TableFilterData } from '@/types/Base';
import { getDynamicRangesWithConditions, removeDynamicRangesByIds } from '@/services/api/ranges';
import type { DynamicRange } from '@/types/Ranges';
import { useStateStore } from '@/stores/state';
import DynamicRangeEditModal from '@/components/ranges/DynamicRangeEditModal.vue';
import { isoDateToFullDate } from '@/utils/date';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();

const dynamicRanges = ref<DynamicRange[]>([]);
const dynamicRangesTotalCount = ref<number>();
const dynamicRangesItemsPerPage = ref(50);
const dynamicRangesPageNumber = ref(1);

const tableHeaders = [
  { value: 'customerName', text: $t('ranges.traderName'), type: 'text', active: true },
  { value: 'traderSiteName', text: $t('ranges.traderSiteName'), type: 'text', active: true },
  { value: 'materialName', text: $t('ranges.materialName'), type: 'text', active: true },
  { value: 'materialAlterCodeCode', text: $t('ranges.alterCodeCode'), type: 'text', active: true },
  { value: 'lastDocumentQuantity', text: $t('ranges.quantity'), type: 'number', active: true },
  { value: 'lastDocumentDate', text: $t('ranges.lastDocumentDate'), type: 'text', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('dynamic-ranges');
  if (storedTableData) {
    dynamicRangesItemsPerPage.value = storedTableData.itemsPerPage;
    dynamicRangesPageNumber.value = storedTableData.page;
  }
  await loadData({
    itemsPerPage: dynamicRangesItemsPerPage.value,
    sortColumn: 'customerName',
    sortAscending: true,
    page: dynamicRangesPageNumber.value,
  });
});

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? tableHeaders[data.column]?.value : 'customerName';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
  };

  await loadData(params);
}

async function loadData(params: QueryParams) {
  const response = await getDynamicRangesWithConditions(params);
  if (response) {
    dynamicRanges.value = response.data.map((item) => ({
      ...item,
      lastDocumentDate: isoDateToFullDate(item.lastDocumentDate),
    }));
    dynamicRangesTotalCount.value = response.count;
  }
}

const modalDeleteRow = ref(false);
const dynamicRangeToDeleteSingle = ref<DynamicRange | null>(null);

function onDeleteRow(item: DynamicRange) {
  dynamicRangeToDeleteSingle.value = item;
  modalDeleteRow.value = true;
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!dynamicRangeToDeleteSingle.value) return;
  await deleteDynamicRanges([dynamicRangeToDeleteSingle.value.id]);
}

async function onDeleteMultiselect(items: DynamicRange[]) {
  const idsToDelete = items.map((item) => item.id);
  if (idsToDelete.length) await deleteDynamicRanges(idsToDelete);
}

async function deleteDynamicRanges(idsToDelete: string[]) {
  const res = await removeDynamicRangesByIds(idsToDelete);
  if (!res && dynamicRanges.value)
    dynamicRanges.value = dynamicRanges.value.filter(
      (dynamicRange) => !idsToDelete.includes(dynamicRange.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const currentDynamicRange = ref<DynamicRange | null>(null);

const createEditModalActive = ref(false);

function onEdit(item: DynamicRange) {
  currentDynamicRange.value = item;
  createEditModalActive.value = true;
}

async function onSave(modifiedData: DynamicRange) {
  if (!dynamicRanges.value) return;

  const index = dynamicRanges.value.findIndex((item) => item.id === modifiedData.id);

  if (index !== -1) dynamicRanges.value[index] = modifiedData;
  else dynamicRanges.value.push(modifiedData);
  createEditModalActive.value = false;
}
</script>
